import React, { useState, useEffect } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import { Modal } from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import {
  AlertTriangle, Shield, Activity, FileText, Zap, Trash2, RefreshCw,
  Cpu, CheckCircle, XCircle, Lock, Sparkles, Info, ChevronDown,
} from 'lucide-react';
import { getAgentlets, purgeAllAgentletAgents } from '../services/api';

/**
 * InferencePage — agent autonomy + kill switch + per-skill routing controls.
 *
 * <h3>Render modes</h3>
 * <ul>
 *   <li><b>Embedded</b> ({@code embedded={true}}): drops the page header
 *       since the parent (Admin Center) already provides one. Used by the
 *       "Agent Inference" tab inside {@code AdminCenterPage} — this is
 *       the ONLY supported entry point as of 2026-05-07.</li>
 *   <li><b>Standalone</b> (default — kept for code reuse and component
 *       tests): renders full page chrome (header + brand icon +
 *       description). The {@code /inference} route was deleted on
 *       2026-05-07 because Admin Center subsumed it; the standalone
 *       branch lives on as a safety net for future re-enablement and
 *       for headless/test invocations. Do not link users here.</li>
 * </ul>
 *
 * <p>Both modes share the same body (agent selector + 4 tabs: Controls /
 * Skills / Stats / Audit Log) so behavioural changes here flow to both
 * surfaces automatically. Backend endpoints under
 * {@code /api/v1/agents/&#123;id&#125;/inference/*} are unchanged by the
 * standalone-route removal.</p>
 *
 * <h3>Theme</h3>
 * <p>Mirrors Policy Config / Workflow Audit / Workflow Intelligence — same
 * BRAND / S / ACCENT tokens, same accent-top-line stat cards, same full-width
 * underline tabs. Visual consistency across the Settings + Governance pages
 * is the goal: an admin moving between Admin Center → Inference Controls
 * shouldn't feel like they crossed a product boundary.</p>
 */

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM (mirrors PolicyConfig / WorkflowAudit / AdminCenter)
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)', soft: '#f8d7d4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const TABS = [
  { id: 'controls', label: 'Controls',  icon: Shield,
    desc: 'Kill switch, global inference mode, and platform-wide cache purge.' },
  { id: 'skills',   label: 'Skills',    icon: Zap,
    desc: 'Per-skill autonomy mode — Human Approval / Autonomous / Platform Decides.' },
  { id: 'stats',    label: 'Stats',     icon: Activity,
    desc: 'Current capability flags and last override timestamp for the selected agent.' },
  { id: 'audit',    label: 'Audit Log', icon: FileText,
    desc: 'Inference-related events for the selected agent — phase changes, gate checks, kill-switch toggles.' },
];

const RISK_ACCENT = {
  critical: ACCENT.rose,
  high:     ACCENT.rose,
  medium:   ACCENT.amber,
  low:      ACCENT.emerald,
};

const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

const GlobalStyles = () => (
  <style>{`
    @keyframes infSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
    @keyframes infPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    .inf-card { transition: all 0.2s cubic-bezier(0.4,0,0.2,1) !important; }
    .inf-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; }
    .inf-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .inf-row:hover { background: ${S[25]} !important; }
    .inf-btn-ghost:hover { background: ${S[100]} !important; }
    .inf-btn-danger:hover { filter: brightness(1.05); }
    .inf-mode-card:hover { border-color: ${BRAND.primary}50 !important; }
  `}</style>
);

export default function InferencePage({ embedded = false } = {}) {
  const {
    agents, loadAgents,
    inferenceOverrides, inferenceStats, inferenceAuditLog,
    loadInferenceData,
    activateKillSwitch, deactivateKillSwitch,
    setInferenceMode, setSkillInferenceMode,
  } = usePlatform();
  const { addToast } = useToast();
  const { user, session, hasPermission } = useAuth();

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [tab, setTab] = useState('controls');
  const [showKillConfirm, setShowKillConfirm] = useState(false);
  const [killReason, setKillReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [skillModes, setSkillModes] = useState({});
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [agentletCount, setAgentletCount] = useState(0);
  const pgSkills = usePaginationState(10);
  const pgAuditLog = usePaginationState(10);

  // Permissions — Platform Admin (global) and Super Admin (customer-scoped)
  // can modify inference controls. The earlier hardcoded `'Super Admin'`
  // string check silently denied Platform Admin every kill-switch / mode /
  // purge control even though PA is the highest-tier role on the platform.
  // We use both legacy role strings (covers stale JWTs from before the
  // claim-rename) AND the wildcard permission set so the gate stays right
  // even if a future role tier inherits 'inference.admin'.
  const isPlatformAdmin = user?.role === 'Platform Admin' || session?.role === 'Platform Admin';
  const isSuperAdmin = user?.role === 'Super Admin' || session?.role === 'Super Admin';
  const canKillSwitch = isPlatformAdmin || isSuperAdmin || hasPermission('inference.admin');
  const canSetAgentMode = canKillSwitch;
  const canSetSkillMode = canKillSwitch;
  const canRead = hasPermission('inference.read');
  const canPurge = canKillSwitch;

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  useEffect(() => {
    loadAgents({ orgId, tenantId });
    getAgentlets({ tenantId }).then((data) => {
      setAgentletCount(data?.total || data?.agentlets?.length || 0);
    }).catch(() => {});
  }, [loadAgents, orgId, tenantId]);

  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) setSelectedAgent(agents[0].id);
  }, [agents, selectedAgent]);

  useEffect(() => {
    if (selectedAgent) loadInferenceData(selectedAgent);
  }, [selectedAgent, loadInferenceData]);

  useEffect(() => {
    const agent = agents.find((a) => a.id === selectedAgent);
    if (agent?.skills) {
      const modes = {};
      agent.skills.forEach((s) => { modes[s.id] = s.inferenceMode || 'human'; });
      setSkillModes(modes);
    }
  }, [selectedAgent, agents]);

  const currentAgent = agents.find((a) => a.id === selectedAgent);
  const killSwitchActive = inferenceOverrides?.killSwitch === true;
  const globalMode = inferenceOverrides?.platformModeEnabled ? 'platform' : 'human';

  // ── Handlers ────────────────────────────────────────────────────────
  const handleActivateKillSwitch = async () => {
    if (!killReason.trim()) { addToast('Please provide a reason', 'warn'); return; }
    setLoading(true);
    try {
      const result = await activateKillSwitch(selectedAgent, killReason);
      if (result?.ok) {
        addToast('Kill switch activated', 'success');
        setShowKillConfirm(false); setKillReason('');
        loadInferenceData(selectedAgent);
      } else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    } finally { setLoading(false); }
  };

  const handleDeactivateKillSwitch = async () => {
    setLoading(true);
    try {
      const result = await deactivateKillSwitch(selectedAgent);
      if (result?.ok) { addToast('Kill switch deactivated', 'success'); loadInferenceData(selectedAgent); }
      else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    } finally { setLoading(false); }
  };

  const handleSetGlobalMode = async (modeId) => {
    setLoading(true);
    try {
      const result = await setInferenceMode(selectedAgent, modeId);
      if (result?.ok) { addToast(`Mode set to "${modeId}"`, 'success'); loadInferenceData(selectedAgent); }
      else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    } finally { setLoading(false); }
  };

  const handleSetSkillMode = async (skillId, mode) => {
    setSkillModes((prev) => ({ ...prev, [skillId]: mode }));
    try {
      const result = await setSkillInferenceMode(selectedAgent, skillId, mode);
      if (result?.ok) addToast(`Skill mode set to "${mode}"`, 'success');
      else addToast(`Failed: ${result?.error || 'Unknown'}`, 'error');
    } catch (err) { addToast(`Failed: ${err.message}`, 'error'); }
  };

  const handlePurgeAllAgents = async () => {
    setPurging(true);
    try {
      const raw = await purgeAllAgentletAgents();
      const result = raw?.data ?? raw;
      if (result?.purged >= 0) {
        addToast(`Purged agents from ${result.purged} of ${result.totalAgentlets} agentlet(s)`, 'success');
        setShowPurgeConfirm(false);
      } else addToast(`Purge failed: ${result?.error || 'Unknown'}`, 'error');
    } catch (err) { addToast(`Purge failed: ${err.message}`, 'error'); }
    finally { setPurging(false); }
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const activeTabMeta = TABS.find((t) => t.id === tab) || TABS[0];

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      // When embedded inside Admin Center, fill the parent. Standalone
      // mode owns the whole page below the breadcrumb (calc against the
      // 56px top bar, same as the other Settings pages).
      height: embedded ? 'auto' : 'calc(100vh - 56px)',
      background: S[50], minHeight: embedded ? 0 : undefined,
    }}>
      <GlobalStyles />

      {/* ═══ HEADER (standalone only) ═════════════════════════════════════ */}
      {!embedded && (
        <div style={{ background: '#fff', flexShrink: 0, padding: '24px 32px 0' }}>
          <PageHeader
            title="Inference Controls"
            subtitle="Manage LLM provider routing and model defaults"
            icon={Zap}
            actions={
              <button onClick={() => loadInferenceData(selectedAgent)} disabled={loading}
                className="inf-btn-ghost"
                style={{
                  ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                  background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                }}>
                <RefreshCw size={13} style={{ animation: loading ? 'infSpin 1s linear infinite' : 'none' }} />
                Refresh
              </button>
            }
          />
        </div>
      )}

      {/* ═══ BODY ═════════════════════════════════════════════════════════ */}
      <div style={{ flex: embedded ? '0 0 auto' : 1, overflowY: embedded ? 'visible' : 'auto',
        padding: embedded ? '20px 24px' : '20px 28px 32px' }}>

        {/* Agent picker — always shown */}
        <AgentPickerStrip
          agents={agents}
          selectedAgent={selectedAgent}
          onChange={setSelectedAgent}
          currentAgent={currentAgent}
          killSwitchActive={killSwitchActive}
          globalMode={globalMode}
        />

        {/* Tabs */}
        {currentAgent && (
          <>
            <div style={{
              display: 'flex', gap: 0,
              background: '#fff',
              border: `1px solid ${S[200]}`, borderBottom: 'none',
              borderTopLeftRadius: 12, borderTopRightRadius: 12, marginTop: 16,
            }}>
              {TABS.map((t) => {
                const Icon = t.icon;
                const isActive = tab === t.id;
                return (
                  <button key={t.id}
                    className={isActive ? '' : 'inf-tab'}
                    onClick={() => setTab(t.id)}
                    title={t.desc}
                    style={{
                      flex: 1, padding: '14px 16px',
                      borderBottom: isActive ? `3px solid ${BRAND.primary}` : `3px solid transparent`,
                      background: 'transparent', border: 'none', borderBottomStyle: 'solid',
                      cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
                      color: isActive ? BRAND.primary : S[500],
                      transition: 'all 0.15s ease',
                      display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center',
                      whiteSpace: 'nowrap',
                    }}>
                    <Icon size={14} strokeWidth={isActive ? 2.2 : 1.8} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab description bar — same gradient strip the platform uses */}
            <div style={{
              padding: '8px 16px', background: BRAND.gradientSubtle,
              borderLeft: `1px solid ${S[200]}`, borderRight: `1px solid ${S[200]}`,
              borderBottom: `1px solid ${S[150]}`,
              fontSize: 11, color: S[500], fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <activeTabMeta.icon size={12} color={S[400]} />
              {activeTabMeta.desc}
            </div>

            {/* Tab body */}
            <div style={{
              background: '#fff',
              border: `1px solid ${S[200]}`, borderTop: 'none',
              borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
              padding: 16,
            }}>
              {tab === 'controls' && (
                <ControlsTab
                  killSwitchActive={killSwitchActive}
                  globalMode={globalMode}
                  loading={loading}
                  canKillSwitch={canKillSwitch}
                  canSetAgentMode={canSetAgentMode}
                  canPurge={canPurge}
                  agentletCount={agentletCount}
                  onActivateKill={() => setShowKillConfirm(true)}
                  onDeactivateKill={handleDeactivateKillSwitch}
                  onSetGlobalMode={handleSetGlobalMode}
                  onPurge={() => setShowPurgeConfirm(true)}
                />
              )}
              {tab === 'skills' && (
                <SkillsTab
                  agent={currentAgent}
                  skillModes={skillModes}
                  canSetSkillMode={canSetSkillMode}
                  onSetSkillMode={handleSetSkillMode}
                  pg={pgSkills}
                />
              )}
              {tab === 'stats' && (
                <StatsTab stats={inferenceStats} formatTs={formatTs} />
              )}
              {tab === 'audit' && (
                <AuditTab log={inferenceAuditLog} pg={pgAuditLog} formatTs={formatTs} />
              )}
            </div>
          </>
        )}

        {!currentAgent && agents.length === 0 && (
          <BodyEmpty msg="No agents configured. Create an agent first to use inference controls." />
        )}
      </div>

      {/* ── Kill Switch Confirmation Modal ── */}
      <Modal
        isOpen={showKillConfirm}
        title="Activate Kill Switch"
        onClose={() => setShowKillConfirm(false)}
        actions={<>
          <button onClick={() => setShowKillConfirm(false)} style={{
            ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: '#fff', border: `1px solid ${S[200]}`, color: S[700],
          }}>Cancel</button>
          <button onClick={handleActivateKillSwitch} disabled={loading || !killReason.trim()}
            style={{
              ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
              background: ACCENT.rose.icon, color: '#fff', border: 'none',
              cursor: loading || !killReason.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !killReason.trim() ? 0.5 : 1,
            }}>
            {loading ? 'Activating…' : 'Confirm activation'}
          </button>
        </>}>
        <div>
          <p style={{ fontSize: 13, color: S[700], lineHeight: 1.55, margin: '0 0 14px 0' }}>
            This will immediately halt all autonomous executions for{' '}
            <strong style={{ color: S[900] }}>{currentAgent?.name}</strong>.
            The agent will require human approval for all actions until deactivated.
          </p>
          <label style={modalLabel}>Reason for activation</label>
          <textarea value={killReason} onChange={(e) => setKillReason(e.target.value)}
            placeholder="Required: document why you're activating the kill switch…"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${S[200]}`, fontSize: 13, fontFamily: 'inherit',
              minHeight: 80, boxSizing: 'border-box', color: S[900], outline: 'none',
            }}/>
        </div>
      </Modal>

      {/* ── Purge Confirmation Modal ── */}
      <Modal
        isOpen={showPurgeConfirm}
        title="Purge agents from all agentlets"
        onClose={() => setShowPurgeConfirm(false)}
        actions={<>
          <button onClick={() => setShowPurgeConfirm(false)} style={{
            ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: '#fff', border: `1px solid ${S[200]}`, color: S[700],
          }}>Cancel</button>
          <button onClick={handlePurgeAllAgents} disabled={purging} style={{
            ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
            background: ACCENT.amber.icon, color: '#fff', border: 'none',
            cursor: purging ? 'not-allowed' : 'pointer',
            opacity: purging ? 0.5 : 1,
          }}>{purging ? 'Purging…' : 'Confirm purge'}</button>
        </>}>
        <p style={{ fontSize: 13, color: S[700], lineHeight: 1.55, margin: '0 0 12px 0' }}>
          This will remove <strong>all cached agent definitions</strong> from every connected agentlet
          ({agentletCount} agentlet{agentletCount !== 1 ? 's' : ''}).
        </p>
        <p style={{ fontSize: 13, color: ACCENT.rose.text, margin: 0, fontWeight: 500 }}>
          All agents must be re-published after purging. Active executions may fail.
        </p>
      </Modal>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   AGENT PICKER STRIP — keeps the agent context visible at the top + shows
   the "at-a-glance" status pills (kill switch, current mode) so you don't
   have to dive into the Controls tab to see the current state.
   ══════════════════════════════════════════════════════════════════════════════ */

function AgentPickerStrip({ agents, selectedAgent, onChange, currentAgent, killSwitchActive, globalMode }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      padding: '14px 16px', display: 'flex', alignItems: 'center',
      gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={fLabelTxt}>Agent</span>
        <select value={selectedAgent || ''} onChange={(e) => onChange(e.target.value)}
          style={{
            padding: '7px 12px', borderRadius: 6, border: `1px solid ${S[200]}`,
            fontSize: 12, color: S[800], background: '#fff', minWidth: 280,
            fontFamily: 'inherit', cursor: 'pointer',
          }}>
          <option value="" disabled>Select an agent…</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>{agent.name}</option>
          ))}
        </select>
      </div>

      {currentAgent && (
        <>
          <div style={{ width: 1, height: 24, background: S[200] }} />
          <StatusPill
            label="Kill switch"
            value={killSwitchActive ? 'ACTIVE' : 'INACTIVE'}
            accent={killSwitchActive ? ACCENT.rose : ACCENT.emerald}
            Icon={killSwitchActive ? AlertTriangle : Shield}
          />
          <StatusPill
            label="Mode"
            value={globalMode === 'platform' ? 'PLATFORM' : 'HUMAN'}
            accent={globalMode === 'platform' ? ACCENT.violet : ACCENT.sky}
            Icon={globalMode === 'platform' ? Sparkles : Lock}
          />
          {currentAgent.skills && (
            <StatusPill
              label="Skills"
              value={`${currentAgent.skills.length}`}
              accent={ACCENT.slate}
              Icon={Zap}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatusPill({ label, value, accent, Icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={fLabelTxt}>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 6,
        background: accent.bg, border: `1px solid ${accent.border}`,
        color: accent.text, fontSize: 10, fontWeight: 700,
        letterSpacing: '0.04em',
        fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      }}>
        <Icon size={11} />
        {value}
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   CONTROLS TAB — kill switch, global mode, purge
   ══════════════════════════════════════════════════════════════════════════════ */

function ControlsTab({
  killSwitchActive, globalMode, loading,
  canKillSwitch, canSetAgentMode, canPurge,
  agentletCount,
  onActivateKill, onDeactivateKill, onSetGlobalMode, onPurge,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Kill switch panel */}
      <Panel
        title="Kill Switch"
        subtitle="Hard halt for all autonomous executions on this agent"
        icon={Shield}
        accent={killSwitchActive ? ACCENT.rose : ACCENT.emerald}
      >
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: killSwitchActive ? ACCENT.rose.bg : ACCENT.emerald.bg,
              border: `1px solid ${killSwitchActive ? ACCENT.rose.border : ACCENT.emerald.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {killSwitchActive
                ? <AlertTriangle size={26} color={ACCENT.rose.icon} />
                : <Shield size={26} color={ACCENT.emerald.icon} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 14, fontWeight: 700, color: S[900],
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                Status
                <span style={{
                  fontSize: 9, fontWeight: 700,
                  padding: '3px 9px', borderRadius: 4,
                  background: killSwitchActive ? ACCENT.rose.bg : ACCENT.emerald.bg,
                  color: killSwitchActive ? ACCENT.rose.text : ACCENT.emerald.text,
                  border: `1px solid ${killSwitchActive ? ACCENT.rose.border : ACCENT.emerald.border}`,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  {killSwitchActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <p style={{ fontSize: 12, color: S[500], margin: '6px 0 0 0', lineHeight: 1.55 }}>
                {killSwitchActive
                  ? 'Agent autonomy disabled. All executions require human approval until the kill switch is deactivated.'
                  : 'Agent operates in its configured autonomy mode. Activate the kill switch to immediately stop all autonomous actions.'}
              </p>
            </div>
          </div>

          {canKillSwitch && killSwitchActive && (
            <button onClick={onDeactivateKill} disabled={loading}
              style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.emerald.icon, color: '#fff', border: 'none',
                opacity: loading ? 0.5 : 1,
              }}>
              {loading ? 'Deactivating…' : 'Deactivate kill switch'}
            </button>
          )}
          {canKillSwitch && !killSwitchActive && (
            <button onClick={onActivateKill} className="inf-btn-danger"
              style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>
              <AlertTriangle size={13} /> Activate kill switch
            </button>
          )}
          {!canKillSwitch && <NoPermissionNote text="Only Super Admin can toggle the kill switch." />}
        </div>
      </Panel>

      {/* Global mode panel */}
      <Panel
        title="Global Inference Mode"
        subtitle="Default autonomy for every skill on this agent (overridable per-skill)"
        icon={Sparkles}
        accent={globalMode === 'platform' ? ACCENT.violet : ACCENT.sky}
      >
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { id: 'human',    label: 'Human Approval', icon: Lock,
                desc: 'Every execution waits for explicit human approval before proceeding.', accent: ACCENT.sky },
              { id: 'platform', label: 'Platform',       icon: Sparkles,
                desc: 'AI decides autonomy per-skill based on risk level. Recommended for production.',
                accent: ACCENT.violet },
            ].map((mode) => {
              const isActive = globalMode === mode.id;
              const ModeIcon = mode.icon;
              return (
                <button key={mode.id}
                  className="inf-mode-card"
                  onClick={() => canSetAgentMode && onSetGlobalMode(mode.id)}
                  disabled={!canSetAgentMode || loading}
                  style={{
                    padding: 16, borderRadius: 10, textAlign: 'left',
                    border: isActive ? `2px solid ${mode.accent.icon}` : `1px solid ${S[200]}`,
                    background: isActive ? mode.accent.bg : '#fff',
                    cursor: canSetAgentMode ? 'pointer' : 'not-allowed',
                    opacity: canSetAgentMode ? 1 : 0.6,
                    transition: 'all 0.15s ease',
                    display: 'flex', alignItems: 'flex-start', gap: 12, fontFamily: 'inherit',
                  }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: isActive ? mode.accent.icon + '22' : mode.accent.bg,
                    border: `1px solid ${mode.accent.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ModeIcon size={16} color={mode.accent.icon} strokeWidth={2} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: S[900] }}>
                        {mode.label}
                      </span>
                      {isActive && (
                        <CheckCircle size={13} color={mode.accent.icon} strokeWidth={2.5} />
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: S[500], margin: '4px 0 0 0', lineHeight: 1.5 }}>
                      {mode.desc}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {!canSetAgentMode && <NoPermissionNote text="Only Super Admin can change inference mode." />}
        </div>
      </Panel>

      {/* Purge panel */}
      <Panel
        title="Purge agents from all connected agentlets"
        subtitle="Clears cached agent definitions across the platform — required after schema migrations"
        icon={Trash2}
        accent={ACCENT.amber}
      >
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Trash2 size={26} color={ACCENT.amber.icon} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: S[900],
                display: 'flex', alignItems: 'center', gap: 8 }}>
                Connected agentlets
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: ACCENT.sky.bg, color: ACCENT.sky.text,
                  border: `1px solid ${ACCENT.sky.border}`,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  {agentletCount}
                </span>
              </div>
              <p style={{ fontSize: 12, color: S[500], margin: '6px 0 0 0', lineHeight: 1.5 }}>
                Removes all cached agent definitions from every connected agentlet.
                Use after database rebuilds or migrations. <strong>Agents must be re-published after purging.</strong>
              </p>
            </div>
          </div>
          {canPurge && (
            <button onClick={onPurge} className="inf-btn-danger"
              style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.amber.icon, color: '#fff', border: 'none',
              }}>
              <Trash2 size={13} /> Purge all agents everywhere
            </button>
          )}
          {!canPurge && <NoPermissionNote text="Only Super Admin can purge agents across agentlets." />}
        </div>
      </Panel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SKILLS TAB — per-skill mode picker
   ══════════════════════════════════════════════════════════════════════════════ */

function SkillsTab({ agent, skillModes, canSetSkillMode, onSetSkillMode, pg }) {
  const skills = agent?.skills || [];
  if (skills.length === 0) {
    return <BodyEmpty msg="No skills configured for this agent." />;
  }
  return (
    <div>
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px,2fr) 110px minmax(180px,1fr) 100px',
          padding: '10px 14px', background: S[50],
          borderBottom: `1px solid ${S[200]}`,
          fontSize: 9, fontWeight: 700, color: S[500],
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Skill</div>
          <div>Risk</div>
          <div>Mode</div>
          <div style={{ textAlign: 'right' }}>Status</div>
        </div>
        {pg.paginate(skills).map((skill) => {
          const ra = RISK_ACCENT[skill.riskLevel] || ACCENT.emerald;
          return (
            <div key={skill.id} className="inf-row" style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(220px,2fr) 110px minmax(180px,1fr) 100px',
              padding: '12px 14px', borderBottom: `1px solid ${S[100]}`, alignItems: 'center',
              fontSize: 12,
            }}>
              <div style={{ fontWeight: 600, color: S[900] }}>
                {skill.name}
              </div>
              <div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: ra.bg, border: `1px solid ${ra.border}`, color: ra.text,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>{skill.riskLevel || 'low'}</span>
              </div>
              <div>
                <select
                  value={skillModes[skill.id] || 'human'}
                  onChange={(e) => onSetSkillMode(skill.id, e.target.value)}
                  disabled={!canSetSkillMode}
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    fontSize: 11, color: S[800], background: '#fff',
                    fontFamily: 'inherit', cursor: canSetSkillMode ? 'pointer' : 'not-allowed',
                  }}>
                  <option value="human">Human Approval</option>
                  <option value="autonomous">Autonomous</option>
                  <option value="platform">Platform Decides</option>
                </select>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: skill.status === 'active' ? ACCENT.emerald.bg : ACCENT.slate.bg,
                  color: skill.status === 'active' ? ACCENT.emerald.text : ACCENT.slate.text,
                  border: `1px solid ${skill.status === 'active' ? ACCENT.emerald.border : ACCENT.slate.border}`,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>{skill.status || 'active'}</span>
              </div>
            </div>
          );
        })}
        <Pagination
          totalItems={skills.length}
          page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          noun="skills"
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   STATS TAB — capability flags + metadata
   ══════════════════════════════════════════════════════════════════════════════ */

function StatsTab({ stats, formatTs }) {
  if (!stats) {
    return <BodyEmpty msg="No stats available. Select an agent to load inference statistics." />;
  }
  const items = [
    { label: 'Kill Switch',     value: stats.killSwitch ? 'Active' : 'Inactive',
      accent: stats.killSwitch ? ACCENT.rose : ACCENT.emerald,
      icon: stats.killSwitch ? AlertTriangle : Shield },
    { label: 'Self-Execute',    value: stats.canSelfExecute ? 'Enabled' : 'Disabled',
      accent: stats.canSelfExecute ? ACCENT.emerald : ACCENT.slate, icon: Cpu },
    { label: 'Platform Execute', value: stats.canPlatformExecute ? 'Enabled' : 'Disabled',
      accent: stats.canPlatformExecute ? ACCENT.emerald : ACCENT.slate, icon: Sparkles },
    { label: 'Self Mode',       value: stats.selfModeEnabled ? 'Enabled' : 'Disabled',
      accent: stats.selfModeEnabled ? ACCENT.emerald : ACCENT.slate, icon: Cpu },
    { label: 'Platform Mode',   value: stats.platformModeEnabled ? 'Enabled' : 'Disabled',
      accent: stats.platformModeEnabled ? ACCENT.emerald : ACCENT.slate, icon: Sparkles },
    { label: 'Last Override',   value: stats.lastOverrideAt ? formatTs(stats.lastOverrideAt) : 'Never',
      accent: ACCENT.slate, icon: FileText },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: 12,
    }}>
      {items.map((item) => (
        <div key={item.label} className="inf-card" style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: '16px 18px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: item.accent.icon, opacity: 0.15 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{
                fontSize: 9, fontWeight: 700, color: item.accent.text,
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
              }}>{item.label}</div>
              <div style={{
                fontSize: 18, fontWeight: 800, color: S[900], lineHeight: 1.05,
                letterSpacing: '-0.01em',
              }}>{item.value}</div>
            </div>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: item.accent.bg, border: `1px solid ${item.accent.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <item.icon size={15} color={item.accent.icon} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   AUDIT TAB — inference event log
   ══════════════════════════════════════════════════════════════════════════════ */

function AuditTab({ log, pg, formatTs }) {
  const list = Array.isArray(log) ? log : [];
  if (list.length === 0) {
    return <BodyEmpty msg="No audit entries found for this agent." />;
  }
  return (
    <div style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '180px 110px 130px 1fr',
        padding: '10px 14px', background: S[50],
        borderBottom: `1px solid ${S[200]}`,
        fontSize: 9, fontWeight: 700, color: S[500],
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        <div>Timestamp</div>
        <div>Category</div>
        <div>Action</div>
        <div>Details</div>
      </div>
      {pg.paginate(list).map((entry, idx) => {
        const detailMsg = typeof entry.details === 'object' && entry.details !== null
          ? (entry.details.message || JSON.stringify(entry.details))
          : String(entry.details || '—');
        const inferredAction = entry.action || (
          detailMsg.includes('Phase advanced') ? 'Phase Change'
          : detailMsg.includes('Phase gate') ? 'Gate Check'
          : detailMsg.includes('decision:') ? 'Skill Review'
          : detailMsg.includes('Eval suite') ? 'Evaluation'
          : detailMsg.includes('pushed') ? 'Skill Push'
          : detailMsg.includes('Assigned') ? 'Assignment'
          : detailMsg.includes('kill') ? 'Kill Switch'
          : 'Event'
        );
        return (
          <div key={entry.id || idx} className="inf-row" style={{
            display: 'grid',
            gridTemplateColumns: '180px 110px 130px 1fr',
            padding: '10px 14px', borderBottom: `1px solid ${S[100]}`, alignItems: 'center',
            fontSize: 12,
          }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: S[600], fontSize: 10 }}>
              {formatTs(entry.createdAt)}
            </div>
            <div>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`,
                color: ACCENT.sky.text,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                fontFamily: "'IBM Plex Mono', monospace",
              }}>{String(entry.category || 'inference')}</span>
            </div>
            <div style={{ fontWeight: 600, color: S[800] }}>{inferredAction}</div>
            <div style={{
              color: S[600], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={detailMsg}>
              {detailMsg}
            </div>
          </div>
        );
      })}
      <Pagination
        totalItems={list.length}
        page={pg.page} pageSize={pg.pageSize}
        onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
        noun="entries"
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function Panel({ title, subtitle, icon: Icon, accent, children }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="inf-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', background: S[25],
        borderBottom: `1px solid ${S[100]}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={14} color={a.icon} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: S[900], letterSpacing: '-0.005em' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function BodyEmpty({ msg }) {
  return (
    <div style={{
      padding: 48, textAlign: 'center', color: S[500],
      background: '#fff', border: `1px dashed ${S[200]}`, borderRadius: 12,
    }}>
      <Info size={28} color={S[300]} />
      <div style={{ fontSize: 13, marginTop: 12, color: S[700] }}>{msg}</div>
    </div>
  );
}

function NoPermissionNote({ text }) {
  return (
    <p style={{
      fontSize: 11, color: S[400], fontStyle: 'italic', margin: '12px 0 0 0',
    }}>
      <Lock size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
      {text}
    </p>
  );
}

const fLabelTxt = {
  fontSize: 10, fontWeight: 700, color: S[500],
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const modalLabel = {
  display: 'block', fontSize: 11, fontWeight: 700, color: S[600],
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
};
